import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography, Modal, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import { createGroupLessonTemplate } from '../../../../api/lesson';
import TicketSelectModal from './TicketSelectModal';

import {
  CreateContainer,
  ModalHeader,
  ModalHeaderTitle,
  CreateContent,
  FormRow,
  FormField,
  ModalFooter,
} from './GroupLessonTemplateStyled';

const LabelWithTooltip = ({ label, tooltip, required }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '4px' }}>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>
      {label}{required && <span style={{ color: '#ef5350', marginLeft: '2px' }}>*</span>}
    </Typography>
    {tooltip && (
      <Tooltip title={<Typography sx={{ fontSize: '13px', lineHeight: 1.5 }}>{tooltip}</Typography>}
        arrow placement="top"
        componentsProps={{ tooltip: { sx: { backgroundColor: '#333', padding: '8px 14px', maxWidth: '260px', borderRadius: '6px' } } }}>
        <HelpOutlineIcon sx={{ fontSize: 15, color: '#bbb', cursor: 'help', '&:hover': { color: '#4AB3BC' } }} />
      </Tooltip>
    )}
  </Box>
);

const INITIAL_FORM = {
  templateName: '',
  className: '',
  classDuration: '',
  ticketDeductionCount: '',
  membersMaxCount: '',
  membersWaitMaxCount: '',
  ticketIds: [],
};

const TemplateFormModal = ({ open, onClose, tickets = [] }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);

  const [form, setForm] = useState(INITIAL_FORM);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setTicketModalOpen(false);
    }
  }, [open]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value.replace(/[^0-9]/g, '') }));
  };

  const createMutation = useMutation({
    mutationFn: data => createGroupLessonTemplate(data, user.token),
    onSuccess: () => {
      toast.success('그룹수업이 생성되었습니다');
      queryClient.invalidateQueries({ queryKey: ['groupLessonTemplates'] });
      onClose();
    },
  });

  const handleSubmit = () => {
    if (!form.templateName.trim()) {
      toast.error('그룹수업명을 입력해주세요');
      return;
    }

    createMutation.mutate({
      branchId: user.branch?.branchId,
      templateName: form.templateName.trim(),
      className: form.className.trim() || null,
      classDuration: Number(form.classDuration) || 50,
      ticketDeductionCount: Number(form.ticketDeductionCount) || 1,
      membersMaxCount: Number(form.membersMaxCount) || 0,
      membersWaitMaxCount: Number(form.membersWaitMaxCount) || 0,
      ticketIds: form.ticketIds,
    });
  };

  const selectedTicketCount = form.ticketIds.length;

  return (
    <>
      <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
        <CreateContainer>
          <ModalHeader>
            <ModalHeaderTitle>그룹수업 만들기</ModalHeaderTitle>
            <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
          </ModalHeader>

          <CreateContent>
            {/* 그룹수업명 (전체 너비) */}
            <Box sx={{ mb: '18px' }}>
              <LabelWithTooltip label="그룹수업명" tooltip="목록에서 구분할 이름입니다" required />
              <TextField
                size="small"
                fullWidth
                placeholder="그룹수업명을 입력해 주세요."
                value={form.templateName}
                onChange={e => handleChange('templateName', e.target.value)}
              />
            </Box>

            {/* 수강권 */}
            <FormRow>
              <FormField>
                <LabelWithTooltip label="수강권 선택" tooltip="등록할 수업에 사용할 수강권입니다" />
                <Box
                  onClick={() => setTicketModalOpen(true)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '40px',
                    px: '14px',
                    border: '1px solid #c4c4c4',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: selectedTicketCount > 0 ? '#f0f8f8' : '#fff',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: '#4AB3BC', boxShadow: '0 0 0 1px rgba(74,179,188,0.1)' },
                  }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: selectedTicketCount > 0 ? 500 : 400, color: selectedTicketCount > 0 ? '#4AB3BC' : '#999' }}>
                    {selectedTicketCount > 0 ? `${selectedTicketCount}개 수강권 선택됨` : '수강권을 선택해 주세요'}
                  </Typography>
                  <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />
                </Box>
              </FormField>
            </FormRow>

            {/* 수업 시간 + 차감 횟수 + 정원 + 대기 정원 */}
            <FormRow>
              <FormField>
                <LabelWithTooltip label="수업 시간" tooltip="수업 1회의 진행 시간 (분)" />
                <TextField
                  size="small"
                  fullWidth
                  placeholder="0"
                  value={form.classDuration}
                  onChange={e => handleNumberChange('classDuration', e.target.value)}
                  InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>분</span> }}
                />
              </FormField>
              <FormField>
                <LabelWithTooltip label="차감 횟수" tooltip="예약 시 수강권에서 차감되는 횟수" />
                <TextField
                  size="small"
                  fullWidth
                  placeholder="0"
                  value={form.ticketDeductionCount}
                  onChange={e => handleNumberChange('ticketDeductionCount', e.target.value)}
                  InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>회</span> }}
                />
              </FormField>
              <FormField>
                <LabelWithTooltip label="정원" tooltip="수업에 예약할 수 있는 최대 인원" />
                <TextField
                  size="small"
                  fullWidth
                  placeholder="0"
                  value={form.membersMaxCount}
                  onChange={e => handleNumberChange('membersMaxCount', e.target.value)}
                  InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>명</span> }}
                />
              </FormField>
              <FormField>
                <LabelWithTooltip label="대기" tooltip="정원 초과 시 대기할 수 있는 최대 인원" />
                <TextField
                  size="small"
                  fullWidth
                  placeholder="0"
                  value={form.membersWaitMaxCount}
                  onChange={e => handleNumberChange('membersWaitMaxCount', e.target.value)}
                  InputProps={{ endAdornment: <span style={{ color: '#999', fontSize: '14px' }}>명</span> }}
                />
              </FormField>
            </FormRow>

          </CreateContent>

          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={onClose}>
              취소
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? '생성 중...' : '생성 완료'}
            </CommonModalButton>
          </ModalFooter>
        </CreateContainer>
      </Modal>

      <TicketSelectModal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        tickets={tickets}
        selectedIds={form.ticketIds}
        onChangeIds={ids => handleChange('ticketIds', ids)}
      />

    </>
  );
};

export default TemplateFormModal;
